'use client'

import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react'
import styles from './ua.module.scss'
import InventoryTile from './InventoryTile'
import { genInvUrl, getFiltersFromParams, searchInventory } from '@/data/Inventory'
import Button from '@/components/button/Button'
import StickyBar from '@/components/nav/StickyBar'
import FiltersComponent from '@/components/filters/FiltersComponent'
import Icon from '@/components/icon/Icon'
import Tiles from '@/components/tile/Tiles'
import VisibilityPixel from '@/components/plugins/VisibilityPixel'
import BudgetFilter from '@/components/filters/BudgetFilter'
import ApiUtils from '@/utils/ApiUtils'
import { useRouter } from 'next/navigation'
import useEventListener, { emitEvent } from '@/components/plugins/EventListener'

const ListingWithFilters = forwardRef((props, ref) => {
  
  const router = useRouter()
  
  const [init] = useState()
  const [filters,setFilters] = useState(props.filters)
  const [location,setLocation] = useState(props.site?.session?.vehicles?.location)
  const [path,setPath] = useState(props.site.path)
  const [viewed, setViewed] = useState(props.site.session?.vehicles.viewed || [])
  const [inventory,setInventory] = useState(props.inventory ? props.inventory.results : [])
  const [facets,setFacets] = useState(props.inventory ? props.inventory.facets : null)
  const [totalCount,setTotalCount] = useState(props.inventory ? props.inventory.total : 0)
  const [viewCount,setViewCount] = useState(props.inventory ? props.inventory.results.length : 0)
  const [pageNum,setPageNum] = useState(props?.site?.inventory?.urlInfo?.page ? Number(props.site.inventory.urlInfo.page) : 1)
  const [budgetFilterVisible,setBudgetFilterVisible] = useState(true)
  const [budget, setBudget] = useState()
  
  useEventListener("visitor_viewed", event => {
    if (event.detail?.vehicles?.viewed) {
      updateLatestViewed(event.detail?.vehicles?.viewed)
    }
  })

  useEventListener("location_updated", event => {
    if (event.detail.location) {
      searchInventory(props.site, Object.assign({}, filters, { location: event.detail.location }))
    }
  })

  useEventListener("shop_by_budget", event => {
    setBudgetFilterVisible(true)
    window.scrollTo({ top: 0, behavior: "smooth" })
  })
  
  useEffect(() => {
    retrieveFacets(filters)
    ApiUtils.getRequest("/api/visitor/session?vid=" + props.site.vid)
    .then(data => {
      if (data?.vehicles?.viewed) updateLatestViewed(data.vehicles.viewed)
      setTimeout(() => {
        emitEvent("visitor_favorites", data)
      }, 500)
    })
  }, [init])
  
  useEffect(() => {
    if (props.params) {
      let apply = (Object.keys(props.params).length > 0 && Object.keys(props.params).includes("term")) || Object.keys(props.params).length != Object.keys(filters).length || path != window.location.href
      const pageParams = getFiltersFromParams(props)
      if (!apply) {
        Object.keys(pageParams).map(key => {
          if (pageParams[key]?.toString() != filters[key]?.toString()) {
            apply = true
          }
        })
      }
      if (apply) {
        setPageNum(props?.site?.inventory?.urlInfo?.page ? Number(props.site.inventory.urlInfo.page) : 1)
        onApplyFilter(Object.assign({}, pageParams, { term: pageParams.term }), true, path != window.location.href)
      }
      setPath(window.location.href)
    }
  }, [props.params])

  const setInventoryResults = async() => {
    const urlInfo = await ApiUtils.postRequest("/api/visitor/inventory/save-url-info", {
      vid: props.site.vid
    });

    if (urlInfo?.page) {
      setPageNum(Number(urlInfo.page))
      props.site.inventory.urlInfo = urlInfo

      const query = Object.assign(getFiltersFromParams(props), filters || {})
      const data = await searchInventory(props.site, query, null, "list", true)

      setInventory(data.results)
      setViewCount(data.results.length)

      return data.results
    } else {
      return [];
    }
  }

  const updateLatestViewed = async(vins) => {
    if (vins.length > 0 && viewed?.length != vins.length) {
      setViewed(vins)
      const inventoryResults = await setInventoryResults();
      const targetInventory = inventoryResults.length ? inventoryResults : inventory;
      const recentViewedCount = parseInt(props.site.inventory.recent_viewed_limit || 5)
      const viewedVins = vins.slice(-recentViewedCount)
      searchInventory(props.site, { vin: viewedVins }).then(data => {
        let list = data.results.sort((a,b) => vins.indexOf(b.vin) - vins.indexOf(a.vin))
        setInventory([...list, ...targetInventory.filter(item => !vins.includes(item.vin))])
        setTimeout(() => {
          emitEvent("visitor_viewed_badge", { vehicles: { viewed: vins }})
        }, 500)
      }).catch(err => {
        console.log(err)
      })
    }
  }

  const onApplyFilter = (params, applyLocal, reset) => {
    if (!props.local && params.clear) {
      let pageFilters = getFiltersFromParams(props)
      let reloadPage = false
      if (Object.keys(params).includes('brand') && (!params.brand || params.brand.length == 0)) {
        delete pageFilters['brand']
        reloadPage = true
      }
      if (params.brand && pageFilters.brand && params.brand.length != pageFilters.brand.length) {
        pageFilters.brand = params.brand
        reloadPage = true
      }
      if (Object.keys(params).includes('condition') && (!params.condition || params.condition.length == 0)) {
        delete pageFilters['condition']
        reloadPage = true
      }
      if (params.condition && pageFilters.condition && params.condition.length != pageFilters.condition.length) {
        pageFilters.condition = params.condition
        reloadPage = true
      }
      if (Object.keys(params).includes('model') && (!params.model)) {
        delete pageFilters['model']
        reloadPage = true
      }
      if (Object.keys(params).includes('body') && (!params.body || params.body.length == 0)) {
        delete pageFilters['body']
        reloadPage = true
      }
      if (params.body && pageFilters.body && params.body.length != pageFilters.body.length) {
        pageFilters.body = params.body
        reloadPage = true
      }
      if (Object.keys(params).includes('fuel') && (!params.fuel || params.fuel.length == 0)) {
        delete pageFilters['fuel']
        reloadPage = true
      }
      if (params.fuel && pageFilters.fuel && params.fuel.length != pageFilters.fuel.length) {
        pageFilters.fuel = params.fuel
        reloadPage = true
      }
      if (Object.keys(params).includes('color') && (!params.color || params.color.length == 0)) {
        delete pageFilters['color']
        reloadPage = true
      }
      if (params.color && pageFilters.color && params.color.length != pageFilters.color.length) {
        pageFilters.color = params.color
        reloadPage = true
      }

      if (Object.keys(params).includes('did') && filters.did) {
        delete pageFilters['did']
        reloadPage = true
      }
      if (Object.keys(params).includes('year') && pageFilters.year && (!params.year || params.year.length == 0)) {
        delete pageFilters['year']
        reloadPage = true
      }
      if (Object.keys(params).includes('max_miles') && pageFilters.max_miles && (!params.max_miles || params.max_miles.length == 0)) {
        delete pageFilters['max_miles']
        reloadPage = true
      }
      if (Object.keys(params).includes('max_price') && pageFilters.max_price && (!params.max_price || params.max_price.length == 0)) {
        delete pageFilters['max_price']
        reloadPage = true
      }
      if (Object.keys(params).includes('term')) {
        delete pageFilters['term']
        router.push(genInvUrl(pageFilters, {}, false))
        return
      }
      if (reloadPage) {
        router.push(genInvUrl(pageFilters, {}, false))
        return
      }
    }

    if (!applyLocal && params.condition && params.condition.length == 1) {
      let pageFilters = Object.assign(getFiltersFromParams(props), filters || {}, params)
      pageFilters.condition = params.condition
      router.push(genInvUrl(pageFilters, {}, false))
      return
    }
    if (!applyLocal && params.brand && params.brand.length == 1) {
      let pageFilters = Object.assign(getFiltersFromParams(props), filters || {}, params)
      pageFilters.brand = params.brand
      router.push(genInvUrl(pageFilters, {}, false))
      return
    }
    if (!applyLocal && params.body && params.body.length == 1) {
      let pageFilters = Object.assign(getFiltersFromParams(props), filters || {}, params)
      pageFilters.body = params.body
      router.push(genInvUrl(pageFilters, {}, false))
      return
    }
    if (!applyLocal && params.fuel && params.fuel.length == 1) {
      let pageFilters = Object.assign(getFiltersFromParams(props), filters || {}, params)
      pageFilters.fuel = params.fuel
      router.push(genInvUrl(pageFilters, {}, false))
      return
    }
    if (!applyLocal && params.color && params.color.length == 1) {
      let pageFilters = Object.assign(getFiltersFromParams(props), filters || {}, params)
      pageFilters.color = params.color
      router.push(genInvUrl(pageFilters, {}, false))
      return
    }
    
    if (params.budget) {
      setBudget(params.budget)
    }

    delete params['clear']
    const filterParams = Object.assign({}, reset ? {} : filters, params)

    if (reset && filters.sort && filters.sort.length > 0) {
      filterParams.sort = props.filters.sort
    }

    setFilters(filterParams)
    let query = Object.assign({}, filterParams, { page: pageNum })
    if (!query.location) query.location = location
    const hasUrlInfo = props?.site?.inventory?.urlInfo ? true : false

    searchInventory(props.site, query, null, "list", hasUrlInfo).then(data => {
      setInventory(data.results)
      setViewCount(data.results.length)
      setTotalCount(data.total)
      retrieveFacets(query)
    })
    ApiUtils.postRequest("/api/visitor/inventory/save-filters", {
      vid: props.site.vid,
      filters: {
        path: window.location.href,
        filters: filterParams
      }
    })
  }
  
  const retrieveFacets = (params) => {
    searchInventory(props.site, params, ["make_name","model_name","exterior","year"], "list").then(data => {
      setFacets(data.facets)
    })
   }
  
  // const applyFilters = useMemo(() => debounce(onApplyFilter, 100), [])
  const applyFilters = useCallback(onApplyFilter)
  
  const showMore = () => {
    setPageNum(pageNum + 1)
    let query = Object.assign({}, filters, { page: pageNum + 1 })
    searchInventory(props.site, query, null, "list").then(data => {
      let list = [...inventory, ...data.results]
      setViewCount(list.length)
      setInventory(list)
    })
    ApiUtils.postRequest("/api/visitor/inventory/save-url-info", {
      vid: props.site.vid,
      urlInfo: {
        path: window.location.href.replaceAll("%2C", ","),
        page: pageNum + 1
      }
    })
  }
  
  const toggleBudgetFilter = (e) => {
    setBudgetFilterVisible(!budgetFilterVisible)
    window.scrollTo({ top: 0, behavior: "smooth" })
  }
  
  const ad = (index) => {
    if (props.site.page?.inventory_ads?.rule == "after_items") {
      if ((index+1) % props.site.page?.inventory_ads.indexes == 0) {
        let adIdx = parseInt(index / props.site.page?.inventory_ads.indexes)
        adIdx = adIdx % props.site.page.inventory_ads.list.length
        return props.site.page.inventory_ads.list[adIdx]
      }
    } else if (props.site.page?.inventory_ads?.indexes.includes(index)) {
      let adIdx = props.site.page?.inventory_ads.indexes.indexOf(index)
      adIdx = adIdx % props.site.page.inventory_ads.list.length
      return props.site.page.inventory_ads.list[adIdx]
    }
    return null
  }
  
  useImperativeHandle(ref, () => ({
    applyFilters, toggleBudgetFilter
  }))
  
  return <>
    { props.budgetFilterVisible  &&
    <section>
      <BudgetFilter {...props} listingRef={ref} shadow={true} filters={filters || props.filters} noApply={true}></BudgetFilter>
    </section>
    }
    { !props.noFilterBar &&
    <StickyBar {...props} location={'top'} className={styles.fbar} stickyStyle={ styles.fbars }>
      <section className='--nopad'>
        <FiltersComponent {...props} listingRef={ref} filters={filters || props.filters} facets={facets} hideBudgetButton={props.budgetFilterVisible}></FiltersComponent>
      </section>
    </StickyBar>
    }
    { !props.budgetFilterVisible && budgetFilterVisible &&
    <section>
      <BudgetFilter {...props} listingRef={ref} toggle={true} enabled={filters.max_price}></BudgetFilter>
    </section>
    }
    <section className='body-bg --nopad'>
      <Tiles padded={true}>
        { inventory.map((item,index) => (
          <InventoryTile key={index} index={index} {...props} viewed={viewed} budget={budget} item={item} ad={ad(index)}></InventoryTile>
        ))}
      </Tiles>
    </section>
    { !props.noFilterBar &&
    <section>
      <div className='text-align-center text-sm'>Currently viewing {viewCount} of { totalCount } matches</div>
    </section>
    }
    { viewCount < totalCount && !props.noFilterBar &&
    <section>
      <VisibilityPixel {...props} name={"show_more"} data={{}}>
        <div className='flex-row flex-align-center'>
          <Button theme={['white','inline-flex']} style={{ maxWidth: '80%', minWidth: '240px'}} onPress={e => showMore()} label={ 'Show More' }>
            <span>Show more</span> <Icon {...props} name="chevron-down"></Icon>          
          </Button>
        </div>
      </VisibilityPixel>
    </section>
    }
    <section></section>
  </>
  
})

ListingWithFilters.displayName = "ListingWithFilters"
export default ListingWithFilters;